import classnames from 'classnames';
import { memo } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  CheckboxInput,
  DropdownInput,
  ArrowLeft as leftIcon,
  ArrowRight as rightIcon,
  IcNoDataEvent as noDataEventIcon,
  CalendarIcon as calendarIcon,
} from '@/shared/ui';
import useBookmarkHrChartOptions from './useBookmarkHrChartOptions';

export type BookmarkHrChartOptionsProps = {
  timezoneOffset: number;
  // ecgBookmark/index.tsx also passes these, but the component never reads them - pre-existing.
};

/**
 * Renders the date navigation and legend row beneath the bookmark heart-rate chart. Shows a skeleton while the chart loads, otherwise a previous and next date pair around a dropdown built from dailyCount, plus AFib, artifact and no-data legend entries that are dropped when the selected day holds no data.
 *
 * @param props - Options inputs including the timezone offset used to format the selectable dates.
 * @returns The chart options row, or a skeleton while the heart-rate chart loads.
 */
const BookmarkHrChartOptions = ({ timezoneOffset }: BookmarkHrChartOptionsProps) => {
  const {
    dailyCount,
    isLoadingHrChart,
    isEmptyData,
    dateIndex,
    dropdownOptions,
    formatSelectedDate,
    toggleDisplayRhythmEvent,
    onChangeDate,
    handleClickArrow,
    ENUM_EVENT_ACTION,
  } = useBookmarkHrChartOptions({ timezoneOffset });

  if (isLoadingHrChart) {
    return (
      <div className="navigator-skeleton-container">
        <div className="skeleton-container">
          <Skeleton height="100%" />
        </div>
      </div>
    );
  }

  return (
    <div id="hr-chart-options" className="ecg-options">
      <div className="w-33" />
      <div className="select-date w-33">
        <IconButton
          id="bookmark-previous-date-btn"
          className="me-2"
          isFilled
          disabled={dateIndex === 0}
          iconComponent={<img src={leftIcon} alt="Left icon" />}
          onClick={() => {
            handleClickArrow(false);
          }}
        />
        <DropdownInput
          dropdownToggleId="bookmark-date-dropdown-toggle"
          name="date"
          disabled={!dailyCount?.length}
          options={dropdownOptions}
          value={formatSelectedDate}
          onChange={onChangeDate}
          selectedItemIcon={<img src={calendarIcon} alt="Icon calendar" />}
          showCaret={false}
          direction="up"
          container="body"
        />
        <IconButton
          id="bookmark-next-date-btn"
          className="ms-2"
          isFilled
          disabled={dateIndex === (dailyCount?.length ?? 0) - 1}
          iconComponent={<img src={rightIcon} alt="Right icon" />}
          onClick={() => {
            handleClickArrow(true);
          }}
        />
      </div>
      {isEmptyData ? (
        <div className="w-33" />
      ) : (
        <div className="event-info-container w-33">
          {/* <CheckboxInput
              className={classnames('event-info-checkbox', '--other')}
              id="otherEvent"
              name="otherEvent"
              data={{
                label: 'Other events',
                value: isShowOtherEvent,
              }}
              checked={isShowOtherEvent}
              onChange={() => { toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.OTHER_EVENT); }}
            /> */}
          <CheckboxInput
            className={classnames('event-info-checkbox', '--afib', 'always-visible')}
            id="afibEvent"
            name="afibEvent"
            data={{
              label: 'AFib',
              value: false as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
            }}
            checked={false}
            onChange={() => {
              toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.AFIB_EVENT);
            }}
          />
          <CheckboxInput
            className={classnames('event-info-checkbox', '--artifact', 'always-visible')}
            id="artifact"
            name="artifact"
            data={{
              label: 'Artifact',
              value: false as unknown as string, // pre-existing: CheckboxInput's data.value is string|number, this passes a boolean
            }}
            checked={false}
            onChange={() => {
              toggleDisplayRhythmEvent(ENUM_EVENT_ACTION.ARTIFACT_EVENT);
            }}
          />
          <div className={classnames('event-info-checkbox', '--no-data')}>
            <img src={noDataEventIcon} alt="no-data-event" />
            <div>No data</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BookmarkHrChartOptions);
